<template>
  <v-app :style="{ background: $vuetify.theme.themes[$vuetify.theme.dark ? 'dark' : 'light'].background }">
    <top-bar :onShow="showContact"></top-bar>
    <v-main>
      <div class="home-container">
        <!-- Hero Section with Animated Background -->
        <div class="hero-section">
          <div class="animated-background">
            <div class="floating-orbs">
              <div class="orb orb-1"></div>
              <div class="orb orb-2"></div>
              <div class="orb orb-3"></div>
              <div class="orb orb-4"></div>
            </div>
          </div>

          <div class="hero-content">
            <div class="hero-badge">
              <v-chip small color="primary" outlined>
                <v-icon left small>mdi-code-tags</v-icon>
                Available for Freelance
              </v-chip>
            </div>

            <h1 class="hero-title">
              Creating Digital
              <span class="gradient-text">Experiences</span>
              That Matter
            </h1>

            <p class="hero-subtitle">
              Full-Stack Developer & UI/UX Designer crafting modern web solutions
              with cutting-edge technologies and user-centered design principles.
            </p>

            <div class="hero-stats">
              <div class="stat-item">
                <span class="stat-number">50+</span>
                <span class="stat-label">Projects Delivered</span>
              </div>
              <div class="stat-item">
                <span class="stat-number">3+</span>
                <span class="stat-label">Years Experience</span>
              </div>
              <div class="stat-item">
                <span class="stat-number">100%</span>
                <span class="stat-label">Client Satisfaction</span>
              </div>
            </div>

            <div class="hero-actions">
              <v-btn
                x-large
                rounded
                color="primary"
                class="hero-btn primary-btn"
                @click="submit('Projects', 'blog')"
              >
                <v-icon left>mdi-rocket-launch</v-icon>
                Explore My Work
              </v-btn>

              <v-btn
                x-large
                rounded
                outlined
                color="primary"
                class="hero-btn secondary-btn"
                @click="showContact"
              >
                <v-icon left>mdi-message</v-icon>
                <span>Let's Connect</span>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Modern Navigation Pills -->
        <div class="navigation-section">
          <div class="nav-container">
            <div class="nav-pills">
              <div
                v-for="item in items"
                :key="item.key"
                class="nav-pill"
                :class="{ 'active': activeKey === item.key }"
                @click="submit(item.name, item.key)"
              >
                <div class="pill-icon">
                  <v-icon>{{ item.icon }}</v-icon>
                </div>
                <span class="pill-text">{{ item.name }}</span>
                <div class="pill-indicator"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Content Sections with Enhanced Transitions -->
        <div class="content-wrapper">
          <div class="section-header">
            <h2 class="section-title">
              {{ getTitle }}
              <div class="title-underline"></div>
            </h2>
          </div>

          <transition name="content-slide" mode="out-in">
            <div class="content-container" :key="activeKey">
              <AboutMe v-if="activeKey === 'about'"></AboutMe>
              <Resume v-if="activeKey === 'resume'"></Resume>
              <Contact v-if="activeKey === 'contact'"></Contact>
              <Projects v-if="activeKey === 'blog'" @show-contact="showContact"></Projects>
              <Walls v-if="activeKey === 'walls'"></Walls>
            </div>
          </transition>
        </div>
      </div>
    </v-main>

    <!-- Footer Component -->
    <Footer
      :navigation-items="items"
      :social-media="socialMedia"
      @navigate="submit"
      @show-contact="showContact"
    />

    <!-- Contact Dialog Component -->
    <ContactDialog
      v-model="showContactOverlay"
      :social-media="socialMedia"
      @form-submitted="handleFormSubmitted"
      @form-error="handleFormError"
    />

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="showSuccessSnackbar"
      :timeout="4000"
      color="success"
      bottom
      right
      rounded
      class="enhanced-snackbar"
    >
      <div class="snackbar-content">
        <v-icon color="white" class="snackbar-icon">mdi-check-circle</v-icon>
        <span>{{ snackbarMessage }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSuccessSnackbar = false" color="white">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import TopBar from "@/components/TopBar.vue";
import AboutMe from "@/components/AboutMe.vue";
import Contact from "@/components/ContactPage.vue";
import Projects from "@/components/ProjectsComponent.vue";
import Resume from "@/components/ResumeComponent.vue";
import ContactDialog from "@/components/ContactDialog.vue";
import Footer from "@/components/Footer.vue";
import Walls from "@/components/WallsComponent.vue";

import { mapMutations } from "vuex";

export default {
  name: "HomeView",
  components: {
    TopBar,
    AboutMe,
    Contact,
    Projects,
    Resume,
    ContactDialog,
    Footer,
    Walls,
  },
  data() {
    return {
      getTitle: "About Me",
      activeKey: "about",
      activeTab: 0,
      showContactOverlay: false,
      showSuccessSnackbar: false,
      snackbarMessage: "",
      items: [
        { name: "About Me", key: "about", icon: "mdi-account-circle" },
        { name: "Resume", key: "resume", icon: "mdi-file-document-outline" },
        { name: "Projects", key: "blog", icon: "mdi-briefcase-variant" },
        { name: "Contact Me", key: "contact", icon: "mdi-email-outline" },
        { name: "Walls", key: "walls", icon: "mdi-heart-outline" },
      ],
      socialMedia: [
        {
          name: 'Telegram',
          icon: 'fab fa-telegram-plane',
          color: '#0088cc',
          url: 'https://t.me/tech_cg',
          description: 'Quick responses & instant communication'
        },
        {
          name: 'WhatsApp',
          icon: 'mdi-whatsapp',
          color: '#25D366',
          url: 'https://wa.me/+233597537575',
          description: 'Direct messaging for urgent projects'
        },
        {
          name: 'LinkedIn',
          icon: 'mdi-linkedin',
          color: '#0077B5',
          url: 'https://linkedin.com/in/kalculus-guy-915192306',
          description: 'Professional networking & collaboration'
        },
        {
          name: 'Email',
          icon: 'mdi-email-outline',
          color: '#EA4335',
          url: 'mailto:calculus069@gmail.com',
          description: 'calculus069@gmail.com - Detailed inquiries'
        }
      ]
    };
  },
  mounted() {
    // Set initial active tab based on route query parameter
    const akey = this.$route.query.akey;
    if (akey) {
      const matchedItem = this.items.find(item => item.key === akey);
      if (matchedItem) {
        this.activeKey = matchedItem.key;
        this.getTitle = matchedItem.name;
        this.activeTab = this.items.findIndex(item => item.key === akey);
      }
    }

    // Add smooth scrolling behavior
    document.documentElement.style.scrollBehavior = 'smooth';

    // Listen for show-contact event from FabBtn component
    this.$root.$on('show-contact', () => {
      this.showContact();
    });
  },
  watch: {
    '$route.query.akey'(newAkey) {
      if (newAkey) {
        const matchedItem = this.items.find(item => item.key === newAkey);
        if (matchedItem) {
          this.activeKey = matchedItem.key;
          this.getTitle = matchedItem.name;
          this.activeTab = this.items.findIndex(item => item.key === newAkey);
        }
      }
    }
  },
  beforeDestroy() {
    // Clean up event listener
    this.$root.$off('show-contact');
  },
  methods: {
    ...mapMutations(["setSnackbar"]),

    submit(name, key) {
      this.getTitle = name;
      this.activeKey = key;

      // Update URL without reloading the page
      const newQuery = { ...this.$route.query, akey: key };
      this.$router.replace({ query: newQuery }).catch(() => { });

      // Smooth scroll to content
      setTimeout(() => {
        const contentElement = document.querySelector('.content-wrapper');
        if (contentElement) {
          contentElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    },

    showContact() {
      this.showContactOverlay = true;
    },

    openLink(url) {
      window.open(url, '_blank');
    },

    // Scroll functionality moved to FabBtn component

    async submitContactForm() {
      if (this.$refs.contactForm.validate()) {
        this.isSubmitting = true;

        try {
          // Simulate API call delay
          await new Promise(resolve => setTimeout(resolve, 2000));

          // In a real application, you would send this data to your backend
          console.log('Form submitted:', this.contactForm);

          // Show success message
          this.snackbarMessage = "Thank you! Your message has been sent successfully. I'll get back to you soon!";
          this.showSuccessSnackbar = true;

          // Reset form
          this.$refs.contactForm.reset();

          // Close dialog after a delay
          setTimeout(() => {
            this.showContactOverlay = false;
          }, 1500);

        } catch (error) {
          // Handle error
          console.error('Error submitting form:', error);
          this.snackbarMessage = "Sorry, there was an error sending your message. Please try again.";
          this.showSuccessSnackbar = true;
        } finally {
          this.isSubmitting = false;
        }
      }
    },

    // Event handlers for ContactDialog component
    handleFormSubmitted(event) {
      this.snackbarMessage = event.message;
      this.showSuccessSnackbar = true;
      console.log('Contact form submitted:', event.formData);
    },

    handleFormError(event) {
      this.snackbarMessage = event.message;
      this.showSuccessSnackbar = true;
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

// Main container with enhanced spacing
.home-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
  overflow-x: hidden;
}

// Hero Section with Animated Background
.hero-section {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  overflow: hidden;

  @media (min-width: $breakpoint-sm) {
    padding: 2rem;
  }
}

.animated-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      rgba(67, 97, 238, 0.03) 0%,
      rgba(72, 149, 239, 0.03) 50%,
      rgba(67, 97, 238, 0.03) 100%
    );
  }
}

.floating-orbs {
  position: absolute;
  width: 100%;
  height: 100%;

  .orb {
    position: absolute;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.1), rgba(72, 149, 239, 0.1));
    backdrop-filter: blur(10px);
    animation: float 20s ease-in-out infinite;

    &.orb-1 {
      width: 300px;
      height: 300px;
      top: 10%;
      left: -10%;
      animation-delay: 0s;
    }

    &.orb-2 {
      width: 200px;
      height: 200px;
      top: 60%;
      right: -5%;
      animation-delay: -5s;
    }

    &.orb-3 {
      width: 150px;
      height: 150px;
      top: 30%;
      right: 20%;
      animation-delay: -10s;
    }

    &.orb-4 {
      width: 100px;
      height: 100px;
      bottom: 20%;
      left: 10%;
      animation-delay: -15s;
    }
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-20px) rotate(120deg); }
  66% { transform: translateY(20px) rotate(240deg); }
}

.hero-content {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
}

.hero-badge {
  margin-bottom: 2rem;

  .v-chip {
    font-weight: 600;
    font-size: 0.875rem;
    height: 36px;
    border-radius: 18px;
    backdrop-filter: blur(10px);
    background-color: rgba(255, 255, 255, 0.1) !important;
  }
}

.hero-title {
  font-size: clamp(3rem, 8vw, 5.5rem);
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: var(--v-textPrimary-base);
  font-family: 'Poppins', sans-serif;

  .gradient-text {
    background: linear-gradient(135deg, $primary, $accent);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 100%;
      height: 3px;
      background: linear-gradient(135deg, $primary, $accent);
      border-radius: 2px;
      animation: expandWidth 2s ease-out 1s forwards;
      transform: scaleX(0);
      transform-origin: left;
    }
  }
}

@keyframes expandWidth {
  to { transform: scaleX(1); }
}

.hero-subtitle {
  font-size: clamp(1.1rem, 2.5vw, 1.4rem);
  line-height: 1.7;
  margin-bottom: 3rem;
  color: var(--v-textSecondary-base);
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
}

.hero-stats {
  display: flex;
  justify-content: center;
  gap: clamp(2rem, 5vw, 4rem);
  margin-bottom: 3rem;

  @media (max-width: $breakpoint-xs) {
    flex-direction: column;
    gap: 1.5rem;
  }
}

.stat-item {
  text-align: center;

  .stat-number {
    display: block;
    font-size: clamp(2rem, 4vw, 2.5rem);
    font-weight: 800;
    color: $primary;
    line-height: 1;
    margin-bottom: 0.5rem;
  }

  .stat-label {
    font-size: 0.9rem;
    color: var(--v-textSecondary-base);
    font-weight: 500;
  }
}

.hero-actions {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: $breakpoint-xs) {
    gap: 1rem;
    justify-content: center;
  }
}

.hero-btn {
  min-width: 200px;
  height: 56px !important;
  font-weight: 700;
  font-size: 1rem;
  text-transform: none;
  letter-spacing: 0.5px;
  transition: all $transition-medium;
  position: relative;
  overflow: hidden;

  &.primary-btn {
    background: linear-gradient(135deg, $primary, $accent) !important;
    box-shadow: 0 8px 25px rgba(67, 97, 238, 0.3);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: all 0.5s;
    }

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 12px 35px rgba(67, 97, 238, 0.4);

      &::before {
        left: 100%;
      }
    }
  }

  &.secondary-btn {
    border: 2px solid $primary;
    background: rgba(67, 97, 238, 0.05) !important;
    backdrop-filter: blur(10px);

    &:hover {
      transform: translateY(-3px);
      background: rgba(67, 97, 238, 0.1) !important;
      box-shadow: 0 8px 25px rgba(67, 97, 238, 0.2);
    }
  }

  @media (max-width: $breakpoint-xs) {
    min-width: 140px;
    font-size: 0.85rem;
    height: 44px !important;
  }
}

// Modern Navigation Pills
.navigation-section {
  padding: 3rem 1rem;

  @media (min-width: $breakpoint-sm) {
    padding: 4rem 2rem;
  }
}

.nav-container {
  max-width: 800px;
  margin: 0 auto;
}

.nav-pills {
  display: flex;
  gap: 1.5rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    height: 0;
  }

  scrollbar-width: none;

  @media (max-width: $breakpoint-xs) {
    gap: 1rem;
  }
}

.nav-pill {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(var(--v-textSecondary-rgb), 0.05);
  border-radius: 16px;
  border: 2px solid rgba(var(--v-textSecondary-rgb), 0.1);
  cursor: pointer;
  transition: all $transition-medium;
  overflow: hidden;
  flex-shrink: 0;
  min-width: 220px;

  @media (max-width: $breakpoint-xs) {
    min-width: 160px;
    padding: 1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba($primary, 0.05), rgba($accent, 0.05));
    opacity: 0;
    transition: all $transition-medium;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: $shadow-medium;
    border-color: rgba($primary, 0.4);
    background: rgba(var(--v-textSecondary-rgb), 0.08);

    &::before {
      opacity: 1;
    }

    .pill-icon {
      transform: scale(1.1);
      background: linear-gradient(135deg, $primary, $accent);
      color: white;
    }

    .pill-indicator {
      width: 100%;
      opacity: 1;
    }
  }

  &.active {
    border-color: $primary;
    background: linear-gradient(135deg, rgba($primary, 0.12), rgba($accent, 0.08));

    &::before {
      opacity: 1;
    }

    .pill-icon {
      background: linear-gradient(135deg, $primary, $accent);
      color: white;
      transform: scale(1.05);
      box-shadow: 0 4px 15px rgba($primary, 0.3);
    }

    .pill-text {
      color: $primary;
      font-weight: 700;
    }

    .pill-indicator {
      width: 100%;
      opacity: 1;
    }
  }
}

.pill-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-textSecondary-rgb), 0.1);
  color: var(--v-textSecondary-base);
  transition: all $transition-medium;
  flex-shrink: 0;

  @media (max-width: $breakpoint-xs) {
    width: 40px;
    height: 40px;
    font-size: 0.9rem;
  }
}

.pill-text {
  font-weight: 600;
  font-size: 1.1rem;
  color: rgba(var(--v-textPrimary-rgb), 0.7);
  transition: all $transition-medium;
  flex: 1;

  @media (max-width: $breakpoint-xs) {
    font-size: 0.85rem;
  }
}

.pill-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 3px;
  background: linear-gradient(135deg, $primary, $accent);
  border-radius: 2px;
  transition: all $transition-medium;
  opacity: 0;
}

// Enhanced Content Section
.content-wrapper {
  padding: 2rem 1rem 4rem;

  @media (min-width: $breakpoint-sm) {
    padding: 3rem 2rem 6rem;
  }
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-title {
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 800;
  color: var(--v-textPrimary-base);
  font-family: 'Poppins', sans-serif;
  position: relative;
  display: inline-block;
  margin-bottom: 0;
}

.title-underline {
  height: 4px;
  width: 100%;
  background: linear-gradient(135deg, $primary, $accent);
  margin: 1rem auto 0;
  border-radius: 2px;
  animation: expandWidth 1s ease-out 0.5s forwards;
  transform: scaleX(0);
  transform-origin: center;
}

.content-container {
  background: var(--v-surface-base);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: $shadow-light;
  transition: all $transition-medium;

  &:hover {
    box-shadow: $shadow-medium;
  }
}

// Content Transitions
.content-slide-enter-active,
.content-slide-leave-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.content-slide-enter {
  opacity: 0;
  transform: translateX(50px);
}

.content-slide-leave-to {
  opacity: 0;
  transform: translateX(-50px);
}

// No duplicate scroll-to-top button needed - using FabBtn component



// Enhanced Snackbar
.enhanced-snackbar {
  .snackbar-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    .snackbar-icon {
      font-size: 1.25rem;
    }
  }
}

// Responsive Design
@media (max-width: $breakpoint-xs) {
  .hero-section {
    min-height: 80vh;
    padding: 1rem;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .hero-stats {
    flex-direction: column;
    gap: 1rem;
  }

  .hero-actions {
    flex-direction: column;
    gap: 1rem;
  }

  .nav-pills {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .navigation-section {
    padding: 2rem 1rem;
  }

  .content-wrapper {
    padding: 1rem;
  }

  .dialog-header {
    padding: 1.5rem;
  }

  .dialog-content {
    padding: 1.5rem !important;
  }
}
</style>
